import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDatePT } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AddressPicker, rememberAddress } from './AddressPicker';
import { Image as ImageIcon, Calendar, MapPin, User, Trash2, Download, CheckSquare, Square, Upload, X, Play, Film, ChevronRight, FolderOpen, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { cldThumb, cldFull } from '../lib/cloudinary';

// Client-side image compression before upload (sequential, mobile-safe)
// Iteratively downscales + lowers quality until file fits target size (~10MB).
// Uses URL.createObjectURL (no base64 copy → safe for 50-80MB ProRAW/HEIF photos that
// would crash readAsDataURL on mobile).
const compressImage = (file, targetBytes = 10 * 1024 * 1024) => {
  return new Promise((resolve) => {
    if (!file.type || !file.type.startsWith('image/')) {
      resolve(file); // Don't compress videos / non-images
      return;
    }
    // Tiny files: don't waste CPU
    if (file.size <= 2 * 1024 * 1024) {
      resolve(file);
      return;
    }
    const objUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      // Attempts: progressively smaller width + lower quality until <= targetBytes
      const attempts = [
        { maxW: 2560, q: 0.92 },
        { maxW: 2560, q: 0.85 },
        { maxW: 2048, q: 0.85 },
        { maxW: 1920, q: 0.82 },
        { maxW: 1920, q: 0.75 },
        { maxW: 1600, q: 0.75 },
      ];
      const canvas = document.createElement('canvas');
      let lastBlob = null;
      try {
        for (let i = 0; i < attempts.length; i++) {
          const { maxW, q } = attempts[i];
          const ratio = Math.min(maxW / img.width, 1);
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', q));
          if (!blob) continue;
          lastBlob = blob;
          if (blob.size <= targetBytes) break; // good enough
        }
      } catch {/* fallthrough */}
      // Free GPU/canvas memory
      canvas.width = 0; canvas.height = 0;
      URL.revokeObjectURL(objUrl);

      if (lastBlob && lastBlob.size < file.size) {
        const cleanName = (file.name || 'foto.jpg').replace(/\.(heic|heif|tiff|png|webp|dng)$/i, '.jpg');
        resolve(new File([lastBlob], cleanName, { type: 'image/jpeg' }));
      } else {
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      resolve(file); // Browser couldn't decode (e.g. HEIC on Android) — upload as-is
    };
    img.src = objUrl;
  });
};

const PhotoGallery = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active address view
  const [activeClient, setActiveClient] = useState(null);
  const [activeAddress, setActiveAddress] = useState(null);
  const [addressPhotos, setAddressPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  // Selection
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploadClient, setUploadClient] = useState('');
  const [uploadAddress, setUploadAddress] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFiles, setUploadFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [clientAddresses, setClientAddresses] = useState([]);
  const fileInputRef = useRef(null);

  // Search & period filter & sort
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all' | '30d' | '3m' | '1y'
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'alpha'

  useEffect(() => {
    fetchSummary();
    fetchClients();
  }, []);

  // Auto-fill upload form from URL params: /photos?client=X&address=Y&upload=1
  // Also opens the address view automatically.
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingUrlParams, setPendingUrlParams] = useState(true);
  useEffect(() => {
    if (!pendingUrlParams || clients.length === 0) return;
    const cParam = searchParams.get('client');
    const aParam = searchParams.get('address');
    const uParam = searchParams.get('upload');
    if (cParam) {
      const client = clients.find(c => c.name === cParam);
      if (client?.addresses) {
        setClientAddresses(client.addresses.map(a => a.street || a.address || a));
      }
      setUploadClient(cParam);
      if (aParam) {
        setUploadAddress(aParam);
        openAddress(cParam, aParam);
      }
      if (uParam === '1') {
        setShowUpload(true);
      }
      setSearchParams({}, { replace: true });
    }
    setPendingUrlParams(false);
  }, [clients, pendingUrlParams, searchParams, setSearchParams]);

  const fetchClients = async () => {
    try {
      const res = await axiosInstance.get('/clients/all');
      setClients(res.data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/photos/summary');
      setSummary(res.data);
    } catch (error) {
      toast.error('Erro ao carregar galeria');
    } finally {
      setLoading(false);
    }
  };

  const openAddress = async (client, address) => {
    setActiveClient(client);
    setActiveAddress(address);
    setSelectedPhotos(new Set());
    setLoadingPhotos(true);
    try {
      const res = await axiosInstance.get('/photos/by-address', {
        params: { client, address }
      });
      setAddressPhotos(res.data.photos || []);
    } catch (error) {
      toast.error('Erro ao carregar fotos');
    } finally {
      setLoadingPhotos(false);
    }
  };

  const goBack = () => {
    setActiveClient(null);
    setActiveAddress(null);
    setAddressPhotos([]);
    setSelectedPhotos(new Set());
  };

  // Selection handlers
  const togglePhotoSelection = (idx) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPhotos(prev => {
      if (prev.size === addressPhotos.length) return new Set();
      return new Set(addressPhotos.map((_, i) => i));
    });
  };

  const getSelectedUrls = () => {
    return [...selectedPhotos].map(idx => addressPhotos[idx]?.url).filter(Boolean);
  };

  // Download
  // Convert Cloudinary URL → File blob for Web Share / programmatic download
  const fetchAsFile = async (url, filename) => {
    const r = await fetch(url, { mode: 'cors' });
    if (!r.ok) throw new Error(`fetch ${r.status}`);
    const blob = await r.blob();
    return new File([blob], filename || 'foto.jpg', { type: blob.type || 'image/jpeg' });
  };

  // Trigger a direct anchor download — on iOS Safari + Android Chrome this lets user save to Photos
  const triggerDownload = (objectUrl, filename) => {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || 'foto.jpg';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSingle = async (url, filename) => {
    if (!url) return;
    const fname = filename || 'foto_cardoso.jpg';
    try {
      // Try Web Share with files (best on iOS — user can pick "Save to Photos")
      const file = await fetchAsFile(url, fname);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: fname });
          return;
        } catch (e) {
          if (e.name === 'AbortError') return; // user cancelled
        }
      }
      // Fallback: blob download (works on Android, desktop)
      const objectUrl = window.URL.createObjectURL(file);
      triggerDownload(objectUrl, fname);
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 10000);
    } catch {
      // Last resort: direct URL anchor (CORS-bypass via redirect)
      window.open(url, '_blank');
    }
  };

  const handleDownloadSelected = async () => {
    const photos = [...selectedPhotos]
      .map(idx => addressPhotos[idx])
      .filter(p => p && p.url);
    if (photos.length === 0) { toast.error('Selecione pelo menos uma foto'); return; }

    setDownloading(true);
    try {
      // Single file → use shared single handler (Web Share API or download)
      if (photos.length === 1) {
        await handleDownloadSingle(photos[0].url, photos[0].filename || 'foto_cardoso.jpg');
        setSelectedPhotos(new Set());
        return;
      }

      // Multiple files: try Web Share API with files (iOS Photos picker, Android share sheet)
      if (navigator.canShare) {
        setDownloading(true);
        try {
          const files = [];
          for (let i = 0; i < photos.length; i++) {
            const p = photos[i];
            try {
              const f = await fetchAsFile(p.url, p.filename || `foto_${i+1}.jpg`);
              files.push(f);
            } catch {/* skip */}
          }
          if (files.length > 0 && navigator.canShare({ files })) {
            try {
              await navigator.share({ files, title: `${files.length} foto's` });
              toast.success(`Gedeeld via systeem`);
              setSelectedPhotos(new Set());
              return;
            } catch (e) {
              if (e.name === 'AbortError') { setSelectedPhotos(new Set()); return; }
              // fall through to sequential download
            }
          }
        } catch {/* fall through */}
      }

      // Fallback: download each file individually (saves to gallery on mobile, Downloads folder on desktop)
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        try {
          const file = await fetchAsFile(p.url, p.filename || `foto_${i+1}.jpg`);
          const objectUrl = window.URL.createObjectURL(file);
          triggerDownload(objectUrl, p.filename || `foto_${i+1}.jpg`);
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 10000);
          // Small delay so the browser flushes each download
          await new Promise(r => setTimeout(r, 350));
        } catch {/* continue with next */}
      }
      toast.success(`${photos.length} bestand(en) opgeslagen`);
      setSelectedPhotos(new Set());
    } catch (error) {
      toast.error('Erro ao baixar arquivos');
    } finally {
      setDownloading(false);
    }
  };

  // Delete
  const handleDeletePhoto = async (entryId, photoIndex) => {
    if (!window.confirm('Apagar esta foto?')) return;
    try {
      await axiosInstance.delete(`/time-entries/${entryId}/photos/${photoIndex}`);
      toast.success('Foto apagada');
      openAddress(activeClient, activeAddress);
      fetchSummary();
    } catch (error) {
      toast.error('Erro ao apagar foto');
    }
  };

  const handleDeleteSelected = async () => {
    const photoInfos = [...selectedPhotos].map(idx => addressPhotos[idx]).filter(Boolean);
    if (photoInfos.length === 0) return;
    if (!window.confirm(`Apagar ${photoInfos.length} foto(s)? Não pode ser desfeito.`)) return;

    let deleted = 0;
    const grouped = {};
    photoInfos.forEach(p => {
      if (!grouped[p.entry_id]) grouped[p.entry_id] = [];
      grouped[p.entry_id].push(p.photo_index);
    });
    for (const [entryId, indexes] of Object.entries(grouped)) {
      const sorted = [...indexes].sort((a, b) => b - a);
      for (const idx of sorted) {
        try { await axiosInstance.delete(`/time-entries/${entryId}/photos/${idx}`); deleted++; } catch {}
      }
    }
    if (deleted > 0) toast.success(`${deleted} foto(s) apagada(s)!`);
    setSelectedPhotos(new Set());
    openAddress(activeClient, activeAddress);
    fetchSummary();
  };

  // Upload
  const handleUploadClientChange = (clientName) => {
    setUploadClient(clientName);
    setUploadAddress('');
    const client = clients.find(c => c.name === clientName);
    if (client?.addresses) {
      setClientAddresses(client.addresses.map(a => a.street || a.address || a));
    } else {
      setClientAddresses([]);
    }
  };

  const handleUploadPhotos = async () => {
    if (!uploadClient || !uploadAddress || !uploadFiles?.length) {
      toast.error('Preencha todos os campos e selecione fotos');
      return;
    }

    setUploading(true);
    const files = Array.from(uploadFiles);
    let successful = 0;
    let failed = 0;
    const failedNames = [];

    // Get Cloudinary upload signature (bypass our backend for the big-file upload)
    let signatureData;
    try {
      const sigFD = new FormData();
      sigFD.append('folder', `cardoso_bouw/${uploadClient.replace(/\s+/g, '_')}`);
      const sigRes = await axiosInstance.post('/photos/cloudinary-signature', sigFD, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      signatureData = sigRes.data;
    } catch {
      toast.error('Niet kunnen verbinden met upload service');
      setUploading(false);
      return;
    }

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isVideo = file.type && file.type.startsWith('video/');

        setUploadProgress(`${isVideo ? 'Video' : 'Foto'} ${i + 1} van ${files.length}: ${isVideo ? 'voorbereiden' : 'comprimeren'}...`);
        let toSend = file;
        if (!isVideo) {
          try { toSend = await compressImage(file); } catch { /* fallback */ }
        }

        setUploadProgress(`${isVideo ? 'Video' : 'Foto'} ${i + 1} van ${files.length}: uploaden (${(toSend.size / 1024 / 1024).toFixed(1)} MB)...`);

        // Upload DIRECTLY to Cloudinary (bypasses our backend ingress)
        const cloudFD = new FormData();
        cloudFD.append('file', toSend);
        cloudFD.append('api_key', signatureData.api_key);
        cloudFD.append('timestamp', signatureData.timestamp);
        cloudFD.append('signature', signatureData.signature);
        cloudFD.append('folder', signatureData.folder);

        let cloudResult;
        try {
          // Use fetch directly (no auth header to Cloudinary)
          const resourceType = isVideo ? 'video' : 'image';
          const cloudRes = await fetch(
            `https://api.cloudinary.com/v1_1/${signatureData.cloud_name}/${resourceType}/upload`,
            { method: 'POST', body: cloudFD }
          );
          if (!cloudRes.ok) throw new Error(`Cloudinary ${cloudRes.status}`);
          cloudResult = await cloudRes.json();
        } catch (err) {
          failed++;
          failedNames.push(file.name);
          continue;
        }

        // Save metadata to our backend (small JSON, no body limit issues)
        try {
          await axiosInstance.post('/photos/save-direct-upload', {
            client_name: uploadClient,
            address: uploadAddress,
            description: uploadDescription || '',
            cloudinary_url: cloudResult.secure_url,
            cloudinary_public_id: cloudResult.public_id,
            cloudinary_thumbnail: isVideo
              ? `https://res.cloudinary.com/${signatureData.cloud_name}/video/upload/c_fill,g_auto,h_400,w_400,q_auto:good,f_jpg,so_2/${cloudResult.public_id}.jpg`
              : `https://res.cloudinary.com/${signatureData.cloud_name}/image/upload/c_fill,g_auto,h_400,w_400,q_auto:good,f_auto/${cloudResult.public_id}`,
            file_type: file.type || 'image/jpeg',
            filename: file.name,
            is_video: isVideo,
            duration: cloudResult.duration || 0,
            bytes: cloudResult.bytes || toSend.size,
          });
          successful++;
        } catch {
          failed++;
          failedNames.push(file.name);
        }
      }

      if (successful > 0 && failed === 0) {
        toast.success(`✅ ${successful} bestand(en) verzonden!`);
      } else if (successful > 0 && failed > 0) {
        toast.warning(`${successful} verzonden, ${failed} mislukt: ${failedNames.slice(0, 3).join(', ')}`);
      } else {
        toast.error(`Geen bestanden verzonden. ${failedNames.length > 0 ? 'Mislukt: ' + failedNames.slice(0, 3).join(', ') : ''}`);
      }

      if (successful > 0) {
        rememberAddress(uploadClient, uploadAddress);
        setUploadFiles(null);
        setUploadDescription('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchSummary();
        if (activeClient === uploadClient && activeAddress === uploadAddress) {
          openAddress(activeClient, activeAddress);
        }
        if (failed === 0) setShowUpload(false);
      }
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const totalPhotos = summary.reduce((sum, c) => sum + c.total_photos, 0);

  // Compute period threshold (ISO date string) for filtering — null = no filter
  const periodCutoff = (() => {
    if (periodFilter === 'all') return null;
    const now = new Date();
    const days = periodFilter === '3d' ? 3 : periodFilter === '30d' ? 30 : periodFilter === '3m' ? 92 : periodFilter === '1y' ? 365 : 0;
    if (!days) return null;
    const cutoff = new Date(now.getTime() - days * 24 * 3600 * 1000);
    return cutoff.toISOString().slice(0, 10); // YYYY-MM-DD
  })();

  // Filter summary: search by bedrijf name, klant label, or address; period by latest_date
  const filteredSummary = summary
    .map((b) => {
      const term = searchTerm.trim().toLowerCase();
      const filteredAddrs = (b.addresses || []).filter((a) => {
        // Period filter: address must have at least 1 photo within the cutoff
        if (periodCutoff && (!a.latest_date || a.latest_date < periodCutoff)) return false;
        if (!term) return true;
        const inBedrijf = (b.client || '').toLowerCase().includes(term);
        const inLabel = (a.label || '').toLowerCase().includes(term);
        const inAddr = (a.address || '').toLowerCase().includes(term);
        return inBedrijf || inLabel || inAddr;
      });
      // Sort the addresses inside each bedrijf card too (recent first when chosen)
      const sortedAddrs = sortBy === 'recent'
        ? [...filteredAddrs].sort((a, b2) => (b2.latest_date || '').localeCompare(a.latest_date || ''))
        : filteredAddrs;
      // bedrijf-level latest_date = max of address dates (used for sorting bedrijven)
      const bedrijfLatest = sortedAddrs.reduce((max, a) => (a.latest_date && a.latest_date > max ? a.latest_date : max), '');
      return { ...b, addresses: sortedAddrs, total_photos: sortedAddrs.reduce((s, a) => s + a.total_photos, 0), _latest: bedrijfLatest };
    })
    .filter((b) => b.addresses.length > 0)
    .sort((a, b) => {
      if (sortBy === 'recent') return (b._latest || '').localeCompare(a._latest || '');
      return (a.client || '').toLowerCase().localeCompare((b.client || '').toLowerCase());
    });
  const filteredTotal = filteredSummary.reduce((s, c) => s + c.total_photos, 0);

  // Find the klant label for the currently active address (for breadcrumb).
  const activeKlantLabel = activeClient && activeAddress
    ? (summary.find(b => b.client === activeClient)?.addresses.find(a => a.address === activeAddress)?.label || '')
    : '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      {activeClient && (
        <nav className="flex items-center gap-1.5 text-sm text-gray-500 flex-wrap" aria-label="Breadcrumb" data-testid="photo-breadcrumb">
          <button onClick={goBack} className="hover:text-[#3da5a5] hover:underline" data-testid="breadcrumb-root">
            {t('photoGallery')}
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
          <button onClick={goBack} className="hover:text-[#3da5a5] hover:underline font-medium text-gray-700" data-testid="breadcrumb-bedrijf">
            {activeClient}
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
          <span className="text-gray-800 font-semibold truncate" data-testid="breadcrumb-klant">
            {activeKlantLabel || activeAddress}
            {activeKlantLabel && activeAddress && (
              <span className="ml-1 text-gray-400 font-normal">· {activeAddress}</span>
            )}
          </span>
        </nav>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          {activeClient && (
            <Button variant="ghost" onClick={goBack} className="text-gray-600 hover:text-gray-800 -ml-2" data-testid="back-to-gallery">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-4xl font-bold text-gray-800" data-testid="photo-gallery-title">
              {activeClient ? (activeKlantLabel || activeAddress) : t('photoGallery')}
            </h1>
            {activeClient && (
              <p className="text-gray-500 text-sm mt-1">{activeClient} · {addressPhotos.length} arquivo(s)</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              // When opening from an active address, auto-fill the form
              if (!showUpload && activeClient && activeAddress) {
                setUploadClient(activeClient);
                setUploadAddress(activeAddress);
                const client = clients.find(c => c.name === activeClient);
                if (client?.addresses) {
                  setClientAddresses(client.addresses.map(a => a.street || a.address || a));
                }
              }
              setShowUpload(!showUpload);
            }}
            className="bg-[#3da5a5] hover:bg-[#2d8585]"
            data-testid="upload-photos-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Adicionar Fotos
          </Button>
          {selectedPhotos.size > 0 && (
            <>
              <Button onClick={handleDownloadSelected} disabled={downloading} className="bg-blue-600 hover:bg-blue-700" data-testid="download-selected-btn">
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Bezig...' : `Opslaan (${selectedPhotos.size})`}
              </Button>
              <Button onClick={handleDeleteSelected} variant="destructive" data-testid="delete-selected-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Apagar ({selectedPhotos.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Upload Panel */}
      {showUpload && (
        <Card className="bg-white shadow-sm border-gray-200 border-l-4 border-l-[#3da5a5]" data-testid="upload-panel">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">Adicionar Fotos à Galeria</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowUpload(false)}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label className="text-gray-700">Klant / adres * <span className="text-xs text-gray-400 font-normal">— typ naam, straat of plaats</span></Label>
                <AddressPicker clients={clients} clientName={uploadClient} address={uploadAddress} testId="upload-address-picker"
                  onChange={({ clientName, address }) => { handleUploadClientChange(clientName); setUploadAddress(address); }} />
                {uploadClient && clientAddresses.length === 0 && (
                  <Input
                    value={uploadAddress}
                    onChange={(e) => setUploadAddress(e.target.value)}
                    placeholder="Digite o endereço"
                    className="bg-gray-50 border-gray-200 text-gray-800 mt-2"
                    data-testid="upload-address-manual"
                  />
                )}
              </div>
              <div>
                <Label className="text-gray-700">Descrição</Label>
                <Input
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Descrição opcional"
                  className="bg-gray-50 border-gray-200 text-gray-800"
                />
              </div>
              <div>
                <Label className="text-gray-700">Fotos/Vídeos *</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={(e) => setUploadFiles(e.target.files)}
                  className="bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="upload-file-input"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={handleUploadPhotos}
                disabled={uploading || !uploadClient || !uploadAddress || !uploadFiles?.length}
                className="bg-[#3da5a5] hover:bg-[#2d8585]"
                data-testid="submit-upload-btn"
              >
                {uploading ? uploadProgress || 'Enviando...' : `Enviar ${uploadFiles?.length || 0} Arquivo(s)`}
              </Button>
              <p className="text-xs text-gray-400">Imagens são comprimidas automaticamente</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* GALLERY VIEW: Client/Address List OR Photo Grid */}
      {!activeClient ? (
        // === OVERVIEW: Client > Address cards ===
        summary.length === 0 ? (
          <Card className="bg-white shadow-sm border-gray-200">
            <CardContent className="py-12">
              <div className="text-center">
                <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t('noPhotos')}</p>
                <p className="text-gray-400 text-sm mt-2">Adicione fotos usando o botão acima</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Search + Period Filter */}
            <div className="flex flex-col sm:flex-row gap-2" data-testid="photo-filters">
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Zoek op bedrijf, klant of adres..."
                  className="bg-white border-gray-200 pl-9 pr-9 h-10"
                  data-testid="photo-search"
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2.5 top-2.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" /></svg>
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700" data-testid="photo-search-clear">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="w-full sm:w-[200px] bg-white border-gray-200 h-10" data-testid="photo-period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle periodes</SelectItem>
                  <SelectItem value="3d" data-testid="period-3d">Laatste 3 dagen</SelectItem>
                  <SelectItem value="30d">Laatste 30 dagen</SelectItem>
                  <SelectItem value="3m">Laatste 3 maanden</SelectItem>
                  <SelectItem value="1y">Laatste jaar</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[220px] bg-white border-gray-200 h-10" data-testid="photo-sort-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent" data-testid="sort-recent">Laatst toegevoegd</SelectItem>
                  <SelectItem value="alpha" data-testid="sort-alpha">Bedrijfsnaam (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-gray-500 text-sm" data-testid="photo-count-summary">
              {filteredTotal} arquivo(s) in {filteredSummary.length} bedrijf
              {(searchTerm || periodFilter !== 'all') && (
                <span className="text-gray-400"> · van totaal {totalPhotos} in {summary.length}</span>
              )}
            </p>
            {filteredSummary.length === 0 && (
              <Card className="bg-white shadow-sm border-gray-200">
                <CardContent className="py-10 text-center">
                  <p className="text-gray-500">Geen resultaten gevonden voor je zoekopdracht.</p>
                </CardContent>
              </Card>
            )}
            {filteredSummary.map(clientData => (
              <Card key={clientData.client} className="bg-white shadow-sm border-gray-200" data-testid={`client-card-${clientData.client}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-[#3da5a5]" />
                    {clientData.client}
                    <span className="text-sm font-normal text-gray-400 ml-2">
                      {clientData.total_photos} arquivo(s)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {clientData.addresses.map(addr => (
                      <button
                        key={addr.address}
                        onClick={() => openAddress(clientData.client, addr.address)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-[#3da5a5] hover:bg-[#3da5a5]/5 transition-all text-left group"
                        data-testid={`address-btn-${addr.address}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <MapPin className="w-4 h-4 text-gray-400 group-hover:text-[#3da5a5] flex-shrink-0" />
                          <div className="min-w-0">
                            {addr.label && (
                              <p className="text-gray-800 font-semibold truncate">{addr.label}</p>
                            )}
                            <p className={addr.label ? "text-gray-500 text-xs truncate" : "text-gray-800 font-medium truncate"}>{addr.address}</p>
                            <p className="text-gray-400 text-xs">
                              {addr.total_photos} foto(s)
                              {addr.latest_date && ` · ${formatDatePT(addr.latest_date)}`}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#3da5a5] flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        // === PHOTO GRID: Viewing photos for a specific address ===
        loadingPhotos ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-600">Carregando fotos...</p>
          </div>
        ) : addressPhotos.length === 0 ? (
          <Card className="bg-white shadow-sm border-gray-200">
            <CardContent className="py-12 text-center">
              <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma foto neste endereço</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#3da5a5]" />
                  {addressPhotos.length} arquivo(s)
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  className="text-gray-600 border-gray-300"
                  data-testid="select-all-btn"
                >
                  {selectedPhotos.size === addressPhotos.length
                    ? <><CheckSquare className="w-4 h-4 mr-1 text-[#3da5a5]" /> Desmarcar tudo</>
                    : <><Square className="w-4 h-4 mr-1" /> Selecionar tudo</>
                  }
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {addressPhotos.map((photo, idx) => {
                  const isSelected = selectedPhotos.has(idx);
                  const isVideo = photo.is_video || (photo.file_type && photo.file_type.startsWith('video/'));
                  const thumbUrl = cldThumb(photo.cloudinary_thumbnail || photo.url, 400);
                  return (
                    <div key={idx} className="group relative">
                      <div
                        className={`aspect-square bg-gray-50 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          isSelected ? 'border-[#3da5a5] ring-2 ring-[#3da5a5]/30' : 'border-gray-100 hover:border-gray-300'
                        }`}
                        onClick={() => togglePhotoSelection(idx)}
                      >
                        {isVideo ? (
                          <>
                            <img src={thumbUrl} alt={`Vídeo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy"
                              onError={(e) => { e.target.style.display = 'none'; }} />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                <Play className="w-6 h-6 text-gray-800 ml-1" />
                              </div>
                            </div>
                            <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-xs flex items-center gap-1">
                              <Film className="w-3 h-3" /> Vídeo
                            </div>
                          </>
                        ) : (
                          <img
                            src={thumbUrl}
                            alt={`Foto ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23eee" width="200" height="200"/><text x="50%" y="50%" fill="%23999" text-anchor="middle" dy=".3em">Erro</text></svg>`;
                            }}
                          />
                        )}
                        {/* Selection indicator */}
                        <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          isSelected ? 'bg-[#3da5a5] text-white' : 'bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100'
                        }`}>
                          {isSelected ? '✓' : ''}
                        </div>
                        {/* Actions */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="secondary" className="h-7 w-7 p-0 bg-white/90 hover:bg-white"
                            onClick={(e) => { e.stopPropagation(); if (photo.url) window.open(photo.url, '_blank'); }}>
                            {isVideo ? <Play className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="secondary" className="h-7 w-7 p-0 bg-white/90 hover:bg-white"
                            onClick={(e) => { e.stopPropagation(); if (photo.url) handleDownloadSingle(photo.url, photo.filename || `arquivo_${idx+1}`); }}>
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="destructive" className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.entry_id, photo.photo_index); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {formatDatePT(photo.date)}
                          <span className="mx-1">|</span>
                          <User className="w-3 h-3" />
                          {photo.user_name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
};

export default PhotoGallery;
